import React, { useState } from "react";
import { Accessibility, ChevronLeft, Contrast, Home, Settings2, Type } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { AppRoute } from "../types";
import { useKioskPreferences } from "../services/kioskPreferences";

const ToggleButton: React.FC<{
  active: boolean;
  label: string;
  icon?: React.ReactNode;
  onClick: () => void;
}> = ({ active, label, icon, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className={`w-full h-11 px-3 rounded-xl text-sm font-bold border transition ${
      active
        ? "bg-primary text-white border-primary"
        : "bg-white text-primary border-primary/20 hover:bg-primary/5"
    }`}
  >
    <span className="inline-flex items-center gap-2">
      {icon}
      {label}
    </span>
  </button>
);

export const KioskControls: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    locale,
    setLocale,
    highContrast,
    largeText,
    wheelchairMode,
    toggleHighContrast,
    toggleLargeText,
    toggleWheelchairMode,
    tr,
  } = useKioskPreferences();
  const [open, setOpen] = useState(false);
  const panelId = "kiosk-accessibility-panel";
  const showFloatingLocale = location.pathname !== AppRoute.WELCOME;

  if (location.pathname.startsWith("/admin")) return null;

  return (
    <div
      className={`kiosk-floating-controls fixed z-[90] flex flex-col gap-2 ${
        wheelchairMode ? "right-4 bottom-4" : "right-4 top-4"
      }`}
    >
      {open && (
        <div
          id={panelId}
          role="dialog"
          aria-modal="false"
          aria-label={tr("Options d'accessibilité", "Accessibility options")}
          className="w-[280px] p-3 rounded-2xl bg-white border border-primary/15 shadow-xl"
        >
          <div className="mb-3">
            <p className="font-black text-primary">{tr("Accessibilité", "Accessibility")}</p>
            <p className="text-xs text-primary/60">
              {tr("Réglages instantanés borne", "Instant kiosk settings")}
            </p>
          </div>

          <div className="space-y-2">
            <ToggleButton
              active={highContrast}
              onClick={toggleHighContrast}
              icon={<Contrast size={15} />}
              label={tr("Contraste élevé", "High contrast")}
            />
            <ToggleButton
              active={largeText}
              onClick={toggleLargeText}
              icon={<Type size={15} />}
              label={tr("Texte plus grand", "Larger text")}
            />
            <ToggleButton
              active={wheelchairMode}
              onClick={toggleWheelchairMode}
              icon={<Accessibility size={15} />}
              label={tr("Mode fauteuil", "Wheelchair mode")}
            />
          </div>
        </div>
      )}

      {wheelchairMode && (
        <div className="grid grid-cols-2 gap-2">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="h-12 px-4 rounded-2xl bg-white border border-primary/20 text-primary font-bold shadow-md flex items-center justify-center gap-2"
          >
            <ChevronLeft size={18} />
            {tr("Retour", "Back")}
          </button>
          <button
            type="button"
            onClick={() => navigate(AppRoute.WELCOME)}
            className="h-12 px-4 rounded-2xl bg-primary text-white font-bold shadow-md flex items-center justify-center gap-2"
          >
            <Home size={18} />
            {tr("Accueil", "Home")}
          </button>
        </div>
      )}

      <div className="flex items-center gap-2 justify-end">
        {showFloatingLocale && (
          <div className="flex rounded-2xl overflow-hidden border border-primary/20 bg-white shadow-md">
            <button
              type="button"
              onClick={() => setLocale("fr")}
              aria-pressed={locale === "fr"}
              aria-label="Français"
              className={`h-10 px-3 text-sm font-bold ${
                locale === "fr" ? "bg-primary text-white" : "text-primary"
              }`}
            >
              FR
            </button>
            <button
              type="button"
              onClick={() => setLocale("en")}
              aria-pressed={locale === "en"}
              aria-label="English"
              className={`h-10 px-3 text-sm font-bold ${
                locale === "en" ? "bg-primary text-white" : "text-primary"
              }`}
            >
              EN
            </button>
          </div>
        )}

        <button
          type="button"
          onClick={() => setOpen((prev) => !prev)}
          className="h-10 w-10 rounded-xl bg-white border border-primary/20 text-primary shadow-md flex items-center justify-center"
          aria-label={tr("Options d'accessibilité", "Accessibility options")}
          aria-controls={panelId}
          aria-expanded={open}
          title={tr("Options d'accessibilité", "Accessibility options")}
        >
          {open ? <Accessibility size={18} /> : <Settings2 size={18} />}
        </button>
      </div>
    </div>
  );
};
